import {
  Badge,
  Button,
  Card,
  Flex,
  ScrollArea,
  Skeleton,
  Text,
} from '@mantine/core';
import { useEffect, useState } from 'react';
import { ConfirmModal } from '../../../components/confirm-modal';
import {
  useCancelBankHourRequest,
  useGetBankHoursRequest,
} from '../../../data/hooks/bank-hours-request';
import { successNotification } from '../../../providers/mantine-notifications';
import { formatLocale } from '../../../providers/dayjs-plugins';
import { BankHourRequestStatus } from '../../../models/bank-hours-request';
import { BankHourRequestStatusHumanized } from '../../../utils/constants/bank-hour';
import { formatHours } from '../../../utils/helpers';

interface ListBankHourRequestsProps {
  userId?: number;
  /** change it to reload the list (ex: after creating a request) */
  refreshKey?: number;
}

const STATUS_COLOR: Record<BankHourRequestStatus, string> = {
  [BankHourRequestStatus.PENDING]: 'yellow',
  [BankHourRequestStatus.SUPERVISOR_APPROVED]: 'blue',
  [BankHourRequestStatus.RH_APPROVED]: 'green',
  [BankHourRequestStatus.REPROVED]: 'red',
  [BankHourRequestStatus.CANCELED]: 'gray',
};

// after the RH approval the days are already justified: only the RH can undo
const CANCELABLE = [
  BankHourRequestStatus.PENDING,
  BankHourRequestStatus.SUPERVISOR_APPROVED,
];

export function ListBankHourRequests({
  userId,
  refreshKey = 0,
}: ListBankHourRequestsProps) {
  const {
    fetch: getBankHourRequestFetcher,
    loading: getBankHourRequestLoader,
    response: getBankHourRequestResponse,
  } = useGetBankHoursRequest();

  const { fetch: cancelFetcher, loading: cancelLoader } =
    useCancelBankHourRequest();
  const [toCancel, setToCancel] = useState<number | null>(null);

  function handleCancel() {
    if (toCancel === null) return;
    const requestId = toCancel;
    setToCancel(null);
    cancelFetcher({
      requestId,
      onSuccess: () => {
        successNotification({
          title: 'Solicitação cancelada',
          message: 'Os dias ficam livres para uma nova solicitação.',
        });
        loadRequests();
      },
    });
  }

  async function loadRequests() {
    await getBankHourRequestFetcher({ params: { userId } });
  }

  useEffect(() => {
    const fetchData = async () => {
      await getBankHourRequestFetcher({
        params: {
          userId,
        },
      });
    };
    fetchData();
  }, [userId, refreshKey]);

  if (getBankHourRequestLoader && !getBankHourRequestResponse) {
    return <Skeleton height={100} radius="md" mt={16} />;
  }

  if (!getBankHourRequestResponse?.length) {
    return (
      <Text size="sm" color="dimmed" mt={8}>
        Você ainda não fez nenhuma solicitação.
      </Text>
    );
  }

  return (
    <ScrollArea mt={16} h={getBankHourRequestResponse.length > 1 ? 260 : 170}>
      <ConfirmModal
        opened={toCancel !== null}
        title="Cancelar solicitação"
        confirmLabel="Cancelar solicitação"
        color="red"
        loading={cancelLoader}
        onCancel={() => setToCancel(null)}
        onConfirm={handleCancel}
      >
        Cancelar esta solicitação? Você poderá solicitar os mesmos dias de novo
        depois.
      </ConfirmModal>
      {getBankHourRequestResponse.map((item) => (
        <Card key={item.id} bg="#dbdbdb40" mb={8} radius={'md'}>
          <Flex justify="space-between" align="center" gap="xs">
            <Badge color={STATUS_COLOR[item.status]}>
              {BankHourRequestStatusHumanized[item.status]}
            </Badge>
            <Text size={'sm'} fw={'bold'}>
              {formatHours(item.amount / 60)}
            </Text>
          </Flex>
          <Text size={'sm'} mt={4}>
            {item.description}
          </Text>
          <Flex gap={'xs'}>
            <Text size={'sm'} fw={'bold'}>
              Período:
            </Text>
            <Text size="sm">
              {formatLocale(item.dateBegin, 'DD/MM/YY')} -{' '}
              {formatLocale(item.dateEnd, 'DD/MM/YY')}
            </Text>
          </Flex>
          {item.isPartial && (
            <Flex gap={'xs'}>
              <Text size={'sm'} fw={'bold'}>
                Horário:
              </Text>

              <Text size="sm">
                {formatLocale(item.dateBegin, 'HH:mm')} -{' '}
                {formatLocale(item.dateEnd, 'HH:mm')}
              </Text>
            </Flex>
          )}

          {CANCELABLE.includes(item.status) && (
            <Flex justify="flex-end" mt="xs">
              <Button
                size="xs"
                variant="subtle"
                color="red"
                onClick={() => setToCancel(item.id)}
              >
                Cancelar solicitação
              </Button>
            </Flex>
          )}

          {item.supervisorNote && (
            <Flex mt="md" direction={'column'}>
              <Text size={'xs'} color="dimmed">
                Observação
              </Text>
              <Text size={'sm'}>{item.supervisorNote}</Text>
            </Flex>
          )}
        </Card>
      ))}
    </ScrollArea>
  );
}
